import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { isTerritorialDivision, type OrgUnitDto } from '@pams/shared';
import { fetchScopedOrgTree, sortTerritorialFirst } from '@/lib/orgTree';
import { queryKeys } from '@/lib/queryKeys';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LIST-FILTERS] THE DIVISIONS "ADJUST FILTERS" OFFERS, AND THE NAME OF ANY UNIT IT HOLDS.
 *
 *  ⚠️ TWO ANSWERS, `useRankOptions`' SHAPE. `options` is what may be CHOSEN — active divisions
 *  only, territorial first. `resolve` is what an ALREADY-STORED id renders as, and it reaches every
 *  unit in the caller's scoped tree, inactive ones included: a filter applied last week may name a
 *  division deactivated since, and `lib/selectOptions.ts` records what a value with no label
 *  paints (its own ObjectId). An id `resolve` cannot name is genuinely gone from the caller's
 *  scope, and the modal says "No longer available" for it.
 *
 *  ⚠️ THE SHARED TREE KEY, AND `fetchScopedOrgTree` FOR IT — never a single-page `queryFn`.
 *  `queryKeys.orgUnits.tree()` is one cache entry across every consumer, and whichever mounts first
 *  fills it for the rest; `lib/orgTree.ts`'s header says why one consumer left on page one would
 *  truncate the tree for all of them.
 *
 *  ⚠️ `enabled` IS THE CALLER'S, AND IT IS NOT A NICETY. The list asks for the tree only while the
 *  modal is open or a filter is applied, so every request sequence the list's existing tests
 *  assert is byte-identical when nobody touches the filter.
 *
 *  ⚠️ `SourceCell` IS NOT A CONSUMER, DELIBERATELY. It keeps a chosen inactive division among its
 *  live options and merges the page's grid map into its resolver — two rules this picker does not
 *  want — so sharing it would change the builder.
 *
 *  `isPending` is TanStack's: no answer yet, which is also true while the query is disabled.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type DivisionOption = { readonly value: string; readonly label: string; readonly territorial: boolean };

/** The label carries the code, so a search matches either spelling — `SourceCell`'s rule. */
const labelOf = (unit: Pick<OrgUnitDto, 'name' | 'shortCode'>): string => `${unit.name} (${unit.shortCode})`;

const NO_UNITS: readonly OrgUnitDto[] = Object.freeze([]);

export const useDivisionOptions = ({
  enabled,
}: {
  enabled: boolean;
}): {
  readonly options: readonly DivisionOption[];
  readonly resolve: (id: string) => { name: string } | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly isLoaded: boolean;
} => {
  const { data, isPending, isError, isSuccess } = useQuery({
    queryKey: queryKeys.orgUnits.tree(),
    queryFn: ({ signal }) => fetchScopedOrgTree(signal),
    enabled,
  });

  const units = useMemo(() => data?.items ?? NO_UNITS, [data]);

  /*
   * ⚠️ SORTED BEFORE THE MAP, because a `DivisionOption` carries no `subType` for the comparator to
   * read — `SourceCell`'s [WINGS.1] note. The server's `depth, name` order survives inside each band.
   */
  const options = useMemo(
    () =>
      sortTerritorialFirst(units.filter((unit) => unit.type === 'DIVISION' && unit.isActive)).map(
        (unit): DivisionOption => ({
          value: unit.id,
          label: labelOf(unit),
          territorial: isTerritorialDivision(unit.type, unit.subType),
        }),
      ),
    [units],
  );

  const resolve = useMemo(() => {
    const byId = new Map(units.map((unit) => [unit.id, unit]));
    return (id: string): { name: string } | undefined => {
      const unit = byId.get(id);
      return unit ? { name: labelOf(unit) } : undefined;
    };
  }, [units]);

  return { options, resolve, isPending, isError, isLoaded: isSuccess };
};
